import { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';

const STORAGE_KEY = 'indyanna_tutorial_done_v1';

type Rect = { top: number; left: number; width: number; height: number };
type TooltipSide = 'top' | 'bottom' | 'left' | 'right';

type Step = {
  target?: string;
  tabId?: string;
  showPanel?: boolean;
  title: string;
  description: string;
  side?: TooltipSide;
  padding?: number;
};

const STEPS: Step[] = [
  {
    title: 'Bienvenu sur le configurateur Indyanna Studio',
    description:
      'Personnalisez votre produit en quelques minutes. Ce guide rapide vous présente les fonctionnalités clés.',
  },
  {
    target: 'canvas',
    side: 'right',
    padding: 8,
    title: 'Explorez votre produit en 3D',
    description:
      'Cliquez et faites glisser pour faire pivoter le produit. Utilisez la molette (ou le pincement sur mobile) pour zoomer.',
  },
  {
    target: 'nav-design',
    tabId: 'design',
    showPanel: true,
    side: 'left',
    padding: 6,
    title: 'Appliquez un design',
    description:
      "Parcourez notre galerie et cliquez sur un design pour l\u2019appliquer instantanément sur votre produit.",
  },
  {
    target: 'nav-colors',
    tabId: 'colors',
    showPanel: true,
    side: 'left',
    padding: 6,
    title: 'Personnalisez les couleurs',
    description:
      'Changez la couleur de chaque partie du produit avec la palette ou une valeur hexadécimale.',
  },
  {
    target: 'nav-motif',
    tabId: 'motif',
    showPanel: true,
    side: 'left',
    padding: 6,
    title: 'Ajoutez un motif de fond',
    description:
      "Habillez tout le produit d\u2019un motif répété\u00a0: camouflage, rayures, pois, géométrique\u2026 Un clic suffit.",
  },
  {
    target: 'nav-logos',
    tabId: 'logos',
    showPanel: true,
    side: 'left',
    padding: 6,
    title: 'Importez votre logo',
    description:
      'Chargez votre propre logo (PNG, JPG, SVG). Déplacez-le, redimensionnez-le et faites-le pivoter directement sur le produit.',
  },
  {
    target: 'nav-text',
    tabId: 'text',
    showPanel: true,
    side: 'left',
    padding: 6,
    title: 'Ajoutez du texte',
    description:
      'Saisissez un nom, un numéro ou un slogan. Choisissez la police, la couleur et le contour pour un rendu pro.',
  },
  {
    target: 'finish-btn',
    side: 'top',
    padding: 8,
    title: 'Finalisez votre création',
    description:
      'Quand vous êtes satisfait, cliquez ici. Téléchargez votre fiche PDF et transmettez votre projet directement à notre équipe.',
  },
];

type Spot = { top: number; left: number; width: number; height: number; radius: number };

function getTargetRect(target: string): Rect | null {
  const el = document.querySelector(`[data-tutorial="${target}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

function computeSpot(rect: Rect, padding: number, radius = 10): Spot {
  return {
    top: rect.top - padding,
    left: rect.left - padding,
    width: rect.width + padding * 2,
    height: rect.height + padding * 2,
    radius,
  };
}

const TOOLTIP_W = 300;
const TOOLTIP_H_EST = 210;

function computeTooltipStyle(
  spot: Spot,
  side: TooltipSide,
  vw: number,
  vh: number,
  centerX = false,
): React.CSSProperties {
  const GAP = 18;
  let top = 0;
  let left = 0;

  if (side === 'right') {
    top = spot.top + spot.height / 2 - TOOLTIP_H_EST / 2;
    left = spot.left + spot.width + GAP;
    if (left + TOOLTIP_W > vw - 12) left = spot.left - TOOLTIP_W - GAP;
  } else if (side === 'left') {
    top = spot.top + spot.height / 2 - TOOLTIP_H_EST / 2;
    left = spot.left - TOOLTIP_W - GAP;
    if (left < 12) left = spot.left + spot.width + GAP;
  } else if (side === 'bottom') {
    top = spot.top + spot.height + GAP;
    left = spot.left + spot.width / 2 - TOOLTIP_W / 2;
  } else {
    top = spot.top - TOOLTIP_H_EST - GAP * 2;
    left = spot.left + spot.width / 2 - TOOLTIP_W / 2;
  }

  if (centerX) left = (vw - TOOLTIP_W) / 2;

  top  = Math.max(12, Math.min(top,  vh - TOOLTIP_H_EST - 12));
  left = Math.max(12, Math.min(left, vw - TOOLTIP_W     - 12));

  return { top, left };
}

export function hasDoneTutorial() {
  try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
}

export function resetTutorial() {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
}

type Props = {
  onDone: () => void;
  onActivateTab?: (tabId: string) => void;
};

export default function TutorialOverlay({ onDone, onActivateTab }: Props) {
  const [step, setStep] = useState(0);
  const [primarySpot, setPrimarySpot] = useState<Spot | null>(null);
  const [panelSpot, setPanelSpot]   = useState<Spot | null>(null);
  const [tooltipStyle, setTooltipStyle] = useState<React.CSSProperties>({});
  const [visible, setVisible] = useState(false);
  const [vp, setVp] = useState({ w: window.innerWidth, h: window.innerHeight });
  const stepRef = useRef(step);
  stepRef.current = step;

  const current = STEPS[step];
  const isFirst = step === 0;
  const isLast  = step === STEPS.length - 1;
  const hasSpots = !!primarySpot;

  const updatePositions = useCallback(() => {
    const st = STEPS[stepRef.current];
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const isMobile = vw <= 768;
    setVp({ w: vw, h: vh });

    if (!st.target) { setPrimarySpot(null); setPanelSpot(null); return; }

    const rect = getTargetRect(st.target);
    if (!rect) { setPrimarySpot(null); setPanelSpot(null); return; }

    const pad = st.padding ?? 8;
    const spot = computeSpot(rect, pad, 10);
    setPrimarySpot(spot);

    // On mobile the nav bar is at the bottom → push tooltip above it, centered
    const effectiveSide: TooltipSide = st.showPanel && isMobile ? 'top' : (st.side ?? 'bottom');
    const centerX = st.showPanel && isMobile;
    setTooltipStyle(computeTooltipStyle(spot, effectiveSide, vw, vh, centerX));

    if (st.showPanel) {
      const panelRect = getTargetRect('config-panel');
      setPanelSpot(panelRect ? computeSpot(panelRect, 0, 4) : null);
    } else {
      setPanelSpot(null);
    }
  }, []);

  useLayoutEffect(() => {
    updatePositions();
    const st = STEPS[step];
    if (st.tabId && onActivateTab) onActivateTab(st.tabId);
  }, [step, updatePositions, onActivateTab]);

  // Re-measure after tab content renders (gives panel time to paint)
  useEffect(() => {
    const t = setTimeout(updatePositions, 80);
    return () => clearTimeout(t);
  }, [step, updatePositions]);

  useEffect(() => {
    const onResize = () => updatePositions();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [updatePositions]);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 30);
    return () => clearTimeout(t);
  }, []);

  const handleFinish = useCallback(() => {
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* noop */ }
    onDone();
  }, [onDone]);

  const next = useCallback(() => {
    if (isLast) { handleFinish(); return; }
    setStep(s => s + 1);
  }, [isLast, handleFinish]);

  const prev = useCallback(() => {
    setStep(s => Math.max(0, s - 1));
  }, []);

  return (
    <div
      className={`tuto-overlay ${visible ? 'tuto-overlay--visible' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label="Tutoriel"
    >
      {/* SVG dark mask with cutout holes */}
      <svg
        className="tuto-svg-mask"
        width={vp.w}
        height={vp.h}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <mask id="tuto-cutout-mask">
            <rect width={vp.w} height={vp.h} fill="white" />
            {primarySpot && (
              <rect
                x={primarySpot.left} y={primarySpot.top}
                width={primarySpot.width} height={primarySpot.height}
                rx={primarySpot.radius}
                fill="black"
              />
            )}
            {panelSpot && (
              <rect
                x={panelSpot.left} y={panelSpot.top}
                width={panelSpot.width} height={panelSpot.height}
                rx={panelSpot.radius}
                fill="black"
              />
            )}
          </mask>
        </defs>
        {/* Dark overlay */}
        <rect
          width={vp.w} height={vp.h}
          fill="rgba(0,0,0,0.68)"
          mask="url(#tuto-cutout-mask)"
        />
        {/* Orange ring around primary spotlight */}
        {primarySpot && (
          <rect
            x={primarySpot.left} y={primarySpot.top}
            width={primarySpot.width} height={primarySpot.height}
            rx={primarySpot.radius}
            fill="none"
            stroke="rgba(239,114,24,0.9)"
            strokeWidth="3"
          />
        )}
        {/* Subtle border around panel */}
        {panelSpot && (
          <rect
            x={panelSpot.left} y={panelSpot.top}
            width={panelSpot.width} height={panelSpot.height}
            rx={panelSpot.radius}
            fill="none"
            stroke="rgba(239,114,24,0.35)"
            strokeWidth="2"
          />
        )}
      </svg>

      {/* Progress dots */}
      <div className="tuto-dots">
        {STEPS.map((_, i) => (
          <button
            key={i}
            type="button"
            className={`tuto-dot ${i === step ? 'tuto-dot--active' : ''} ${i < step ? 'tuto-dot--done' : ''}`}
            onClick={() => setStep(i)}
            aria-label={`Étape ${i + 1}`}
          />
        ))}
      </div>

      {/* Tooltip card */}
      <div
        className={`tuto-card ${!hasSpots ? 'tuto-card--centered' : ''}`}
        style={hasSpots ? tooltipStyle : undefined}
      >
        <div className="tuto-card-step">{step + 1} / {STEPS.length}</div>
        <h3 className="tuto-card-title">{current.title}</h3>
        <p className="tuto-card-desc">{current.description}</p>

        <div className="tuto-card-actions">
          {!isFirst && (
            <button type="button" className="tuto-btn tuto-btn--ghost" onClick={prev}>
              Précédent
            </button>
          )}
          <button type="button" className="tuto-btn tuto-btn--primary" onClick={next}>
            {isLast ? 'Commencer !' : isFirst ? 'Démarrer le guide' : 'Suivant'}
          </button>
        </div>

        <button className="tuto-card-skip" type="button" onClick={handleFinish}>
          Passer le tutoriel
        </button>
      </div>
    </div>
  );
}
